/**
 * pages/join-couple.tsx
 *
 * Join Couple screen — enter partner's 6-char invite code.
 * Uses the OTP-style 6-box input per design-language §4.3.
 */
import { useRef, useState, KeyboardEvent, ClipboardEvent } from 'react'
import { useNavigate } from 'react-router-dom'
import { useJoinCouple } from '../hooks/use-couple'

const StepDots = ({ current, total }: { current: number; total: number }) => (
  <div className="flex items-center justify-center gap-2 mb-8">
    {Array.from({ length: total }, (_, i) => (
      <div
        key={i}
        className={`rounded-full transition-all duration-300 ${
          i + 1 === current
            ? 'w-6 h-2 bg-[#D4537E]'
            : 'w-2 h-2 bg-[#F4C0D1]'
        }`}
      />
    ))}
  </div>
)

export default function JoinCouplePage() {
  const navigate = useNavigate()
  const { mutate: joinCouple, isPending, error } = useJoinCouple()

  const [digits, setDigits] = useState<string[]>(Array(6).fill(''))
  const inputRefs = useRef<(HTMLInputElement | null)[]>([])

  const code = digits.join('').toUpperCase()
  const isComplete = code.length === 6

  const focusAt = (index: number) => {
    inputRefs.current[index]?.focus()
  }

  const handleChange = (index: number, value: string) => {
    const char = value.replace(/[^a-zA-Z0-9]/g, '').slice(-1).toUpperCase()
    const next = [...digits]
    next[index] = char
    setDigits(next)
    if (char && index < 5) {
      focusAt(index + 1)
    }
  }

  const handleKeyDown = (index: number, e: KeyboardEvent<HTMLInputElement>) => {
    if (e.key === 'Backspace') {
      if (digits[index]) {
        const next = [...digits]
        next[index] = ''
        setDigits(next)
      } else if (index > 0) {
        focusAt(index - 1)
        const next = [...digits]
        next[index - 1] = ''
        setDigits(next)
      }
    } else if (e.key === 'ArrowLeft' && index > 0) {
      focusAt(index - 1)
    } else if (e.key === 'ArrowRight' && index < 5) {
      focusAt(index + 1)
    }
  }

  const handlePaste = (e: ClipboardEvent<HTMLInputElement>) => {
    e.preventDefault()
    const pasted = e.clipboardData
      .getData('text')
      .replace(/[^a-zA-Z0-9]/g, '')
      .toUpperCase()
      .slice(0, 6)
    const next = Array(6).fill('')
    for (let i = 0; i < pasted.length; i++) {
      next[i] = pasted[i] ?? ''
    }
    setDigits(next)
    const lastFilled = Math.min(pasted.length, 5)
    focusAt(lastFilled)
  }

  const handleSubmit = () => {
    if (!isComplete) return
    joinCouple(
      { invite_code: code },
      {
        onSuccess: ({ data }) => {
          // If the couple already has an anniversary date set by the creator,
          // skip the anniversary setup screen and go straight to home.
          if (data.couple.anniversary_date) {
            navigate('/home')
          } else {
            navigate('/anniversary')
          }
        },
      },
    )
  }

  return (
    <div className="min-h-screen bg-[#FAF8F4] flex flex-col max-w-[430px] mx-auto">
      {/* Header */}
      <header className="sticky top-0 z-40 bg-[#FAF8F4] px-5 pt-[env(safe-area-inset-top)] pb-2">
        <div className="flex items-center gap-2 h-12">
          <button
            type="button"
            onClick={() => navigate(-1)}
            className="flex items-center gap-1 text-[#D4537E] font-['DM_Sans'] text-[15px] active:opacity-60 transition-opacity duration-100 touch-manipulation -ml-1"
          >
            ← Back
          </button>
        </div>
      </header>

      {/* Content */}
      <main className="flex-1 px-5 pb-[88px] overflow-y-auto">
        <div className="animate-fade-in-up">
          <StepDots current={2} total={3} />

          {/* Title */}
          <div className="mb-8 [animation-delay:60ms] animate-fade-in-up">
            <h1 className="font-['Playfair_Display'] text-[28px] font-semibold text-[#2C1810] leading-tight mb-2">
              Enter invite code
            </h1>
            <p className="font-['DM_Sans'] text-[14px] text-[#7A5A62]">
              Ask your partner for their 6-character code.
            </p>
          </div>

          {/* OTP Input */}
          <div className="mb-8 [animation-delay:90ms] animate-fade-in-up">
            <div className="flex justify-center gap-2">
              {digits.map((digit, i) => (
                <input
                  key={i}
                  ref={(el) => { inputRefs.current[i] = el }}
                  type="text"
                  inputMode="text"
                  maxLength={1}
                  value={digit}
                  autoFocus={i === 0}
                  autoComplete="off"
                  autoCorrect="off"
                  spellCheck={false}
                  onChange={(e) => handleChange(i, e.target.value)}
                  onKeyDown={(e) => handleKeyDown(i, e)}
                  onPaste={handlePaste}
                  className={`
                    w-[48px] h-[56px]
                    font-['DM_Mono'] text-[22px] font-medium
                    text-[#D4537E] text-center
                    rounded-[12px]
                    border-[1.5px]
                    outline-none caret-[#D4537E]
                    transition-all duration-150
                    focus:border-[#D4537E] focus:shadow-[0_0_0_3px_rgba(212,83,126,0.12)]
                    select-none touch-manipulation
                    ${
                      digit
                        ? 'border-[#D4537E] bg-[#FBEAF0]'
                        : 'border-[#F4C0D1] bg-[#FAF8F4]'
                    }
                  `}
                />
              ))}
            </div>
          </div>

          {/* Error */}
          {error && (
            <div className="mb-4 [animation-delay:120ms] animate-fade-in-up">
              <p className="font-['DM_Sans'] text-[13px] text-[#C0392B] text-center">
                {error.message}
              </p>
            </div>
          )}

          {/* Join Button */}
          <div className="[animation-delay:120ms] animate-fade-in-up">
            <button
              type="button"
              onClick={handleSubmit}
              disabled={!isComplete || isPending}
              className="
                w-full h-[52px] px-6
                bg-[#D4537E] text-white
                font-['DM_Sans'] text-[15px] font-medium
                rounded-[12px]
                shadow-[0_4px_16px_rgba(212,83,126,0.28)]
                transition-all duration-150 ease-out
                active:bg-[#993556] active:scale-[0.98] active:shadow-none
                disabled:opacity-40 disabled:cursor-not-allowed
                select-none touch-manipulation
              "
            >
              {isPending ? '💕 Joining…' : 'Join'}
            </button>
          </div>

          {/* Back to create */}
          <div className="mt-4 [animation-delay:150ms] animate-fade-in-up">
            <button
              type="button"
              onClick={() => navigate('/create')}
              className="
                w-full h-[52px] px-6
                bg-transparent text-[#7A5A62]
                font-['DM_Sans'] text-[15px] font-medium
                rounded-[12px] border border-[#F4C0D1]
                transition-all duration-150 ease-out
                active:bg-[#FBEAF0]
                select-none touch-manipulation
              "
            >
              Create a new space instead
            </button>
          </div>
        </div>
      </main>
    </div>
  )
}
